import { CheckCircle2 } from "lucide-react";
import { Breadcrumbs, Reveal, SectionHeader, PrimaryButton } from "../components/common";
import { WHY, INDUSTRIES, BUSINESS } from "../data/site";

export default function About() {
  const principles = ["Accuracy through human review", "Context before speed", "Confidentiality by default", "Accountable, founder-led delivery", "Process-led consistency", "Respect for professional terminology"];
  return (
    <>
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "About" }]} />
      <section className="container-page py-12 lg:py-16">
        <span className="eyebrow">About Primevanta Global</span>
        <h1 className="mt-4 max-w-4xl font-heading text-[34px] font-800 leading-[1.08] text-brand-ink md:text-[54px]">Documentation experience built over time—and designed for what comes next.</h1>
        <p className="mt-5 max-w-2xl text-[18px] leading-relaxed text-brand-muted md:text-[20px]">A human-led documentation, quality assurance and business-support partner specialising in healthcare documentation while also supporting legal, academic and business workflows.</p>
      </section>

      <section className="pb-16">
        <div className="container-page overflow-hidden rounded-3xl">
          <img src="https://images.pexels.com/photos/13977666/pexels-photo-13977666.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=1400" alt="Calm, naturally lit workspace with a plant near a large window" className="h-[300px] w-full object-cover lg:h-[440px]" loading="lazy" />
        </div>
      </section>

      <section className="pb-16">
        <div className="container-page grid gap-12 lg:grid-cols-2">
          <Reveal>
            <SectionHeader eyebrow="Our Story" title="A company built around dependable documentation since 2004." />
            <p className="mt-5 text-[16px] leading-relaxed text-brand-muted">Primevanta Global began with healthcare and medical transcription in New Delhi. Over time, the work expanded into clinical documentation support, quality assurance, business documentation, data services and AI-assisted content review.</p>
            <p className="mt-4 text-[16px] leading-relaxed text-brand-muted">Today the business supports healthcare organisations, physicians, hospitals, outsourcing partners, law firms, researchers, universities, authors, publishers and businesses—across India and international markets.</p>
          </Reveal>
          <Reveal delay={1}>
            <div className="rounded-2xl border border-brand-border bg-white p-8">
              <p className="font-serif text-[20px] italic leading-relaxed text-brand-ink">“Technology may support the process, but professional accountability must remain human.”</p>
              <p className="mt-5 text-[14px] font-600 text-brand-primary">{BUSINESS.founder} — Founder</p>
              <p className="text-[13px] text-brand-muted">Editable founder portrait and biography.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-alt py-16">
        <div className="container-page">
          <SectionHeader eyebrow="Core Principles" title="What guides our work" />
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {principles.map((p, i) => (
              <Reveal key={p} delay={i % 3}><div className="flex items-center gap-3 rounded-xl border border-brand-border bg-white px-5 py-4"><CheckCircle2 className="h-5 w-5 shrink-0 text-brand-primary" /><span className="text-[15px] font-500 text-brand-ink">{p}</span></div></Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container-page">
          <SectionHeader eyebrow="Why Primevanta" title="Experience where accuracy, context and accountability matter" />
          <div className="mt-10 grid gap-x-10 gap-y-8 md:grid-cols-2 lg:grid-cols-3">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={i % 3}><div className="border-t-2 border-brand-primary/20 pt-5"><h3 className="font-heading text-[18px] font-700 text-brand-ink">{w.title}</h3><p className="mt-2 text-[15px] leading-relaxed text-brand-muted">{w.text}</p></div></Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-evergreen py-16 text-white">
        <div className="container-page flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
          <div><h2 className="font-heading text-[28px] font-800 md:text-[36px]">Let’s discuss your documentation workflow</h2><p className="mt-2 text-white/70">We will review your requirement and recommend a practical next step.</p></div>
          <PrimaryButton to="/contact" testid="about-cta" className="!bg-brand-gold !text-brand-evergreen hover:!bg-[#c2942f]">Discuss a Project</PrimaryButton>
        </div>
      </section>
    </>
  );
}
